import { useMemo, useState, useEffect } from "react";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  Clock,
  Calendar,
  Users,
  FileText,
  Plus,
  User,
  ChevronRight,
  Mail,
  Send,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { BenefitsDisplaySettingsCard } from "@/components/benefits/BenefitsDisplaySettingsCard";
import PremiumLedgerCard from "@/components/premiums/PremiumLedgerCard";
import { format } from "date-fns";
import { formatDateSafe } from "@/lib/utils";
import { calculateEnrollmentPremium } from "@/lib/enrollmentCalculator";
import { fetchPlanPremiumData } from "@/hooks/usePlanPremiumData";
import { PlanRate } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { useClientPlans } from "@/hooks/useClientPlans";
import BenefitsOverview from "@/pages/broker/ClientDetailComponents/BenefitsTab/BenefitsOverview";
import EnrollmentsTab from "@/pages/broker/ClientDetailComponents/BenefitsTab/EnrollmentsTab";
import AvailablePlans from "@/pages/broker/ClientDetailComponents/BenefitsTab/AvailablePlans";
import OpenEnrollment from "@/pages/broker/ClientDetailComponents/BenefitsTab/OpenEnrollment";
import EmployeeSummary from "@/pages/broker/ClientDetailComponents/BenefitsTab/EmployeeSummary";
import { useLocation } from "wouter";

const BENEFITS_TABS = [
  "overview",
  "enrollments",
  "open-enrollment",
  "employee-summary",
] as const;

export default function Benefits() {
  const [isRequestPlansDialogOpen, setIsRequestPlansDialogOpen] =
    useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState<number | null>(null);
  const [requestType, setRequestType] = useState<string>("");
  const [requestMessage, setRequestMessage] = useState<string>("");
  const [urgency, setUrgency] = useState<string>("normal");

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // URL-based tab persistence using nuqs
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(BENEFITS_TABS).withDefault("overview")
  );

  // Mutation for sending plan request to broker
  const sendPlanRequestMutation = useMutation({
    mutationFn: async (requestData: any) => {
      return await apiRequest("POST", "/api/broker/plan-requests", requestData);
    },
    onSuccess: () => {
      toast({
        title: "Request Sent",
        description:
          "Your plan request has been sent to your broker. They will contact you within 24-48 hours.",
      });
      setIsRequestPlansDialogOpen(false);
      setRequestType("");
      setRequestMessage("");
      setUrgency("normal");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to send request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  // Client-scoped Enrollments (server computes premiums if missing)
  const { data: enrollments = [] } = useQuery<any[]>({
    queryKey: ["/api/enrollments"],
  });

  const employerClientId = useMemo(
    () => (user?.clientId != null ? String(user.clientId) : ""),
    [user]
  );

  // Employer-scoped broker tickets used for "Pending Broker Changes"
  const { data: ticketsView } = useQuery<any>({
    queryKey: ["/api/tasks", employerClientId],
    enabled: !!employerClientId,
    queryFn: async () => {
      if (!employerClientId) return { tasks: [], pagination: {} } as any;
      const res = await fetch(
        `/api/tasks?clientId=${encodeURIComponent(employerClientId)}&limit=50`
      );
      if (!res.ok) return { tasks: [], pagination: {} } as any;
      return res.json();
    },
  });

  // Use shared hook for plan merging
  const { plans: plansData, planYears: planYearsData, isLoading: plansLoading } = useClientPlans(user?.clientId);

  // DEV-310: this mirrored the same dead pattern as broker ClientDetail.tsx
  // - `client_*_customizations` has no writer anywhere, and `_rates`'s
  // `.effectiveDate` field is never populated by any of PlanRates.tsx's 3
  // save paths. Both branches were dead reads. Reduced to a passthrough;
  // kept as a function since it's passed as a prop into shared
  // BenefitsOverview/AvailablePlans components.
  const getEffectivePlan = (basePlan: any) => basePlan;


  // DEV-262: removed getPlanRate here — caller-less duplicate of
  // calculateContributions' job with its own raw fetches (and no buy-up
  // base-plan rows). calculateContributions below uses the shared loader.

  const calculateContributions = async (
    planId: number,
    coverageTier: string,
    employee: any,
    dependentsList: any[]
  ) => {
    const pd = await fetchPlanPremiumData([planId], employerClientId);
    const calc = calculateEnrollmentPremium({
      plan: getEffectivePlan(plansData.find((p: any) => p.id === planId)),
      planRates: pd.ratesByPlanId[planId] || [],
      planContributions: pd.contribsByPlanId[planId] || [],
      employee,
      dependents: dependentsList as any,
      coverageTier,
      allPlanContributions: pd.allPlanContributions,
      allPlanRates: pd.allPlanRates,
    });
    return {
      employerContribution: (calc as any).employerPremium || 0,
      employeeContribution: (calc as any).employeePremium || 0,
    };
  };

  // employerClientId moved above for use by queries

  // Small helper to render async values inline in table cells
  function AsyncValue({
    promiseFn,
    render,
  }: {
    promiseFn: () => Promise<number>;
    render: (val: number) => JSX.Element;
  }) {
    const [val, setVal] = useState<number | null>(null);
    useEffect(() => {
      let mounted = true;
      promiseFn().then((v) => mounted && setVal(v));
      return () => {
        mounted = false;
      };
    }, [promiseFn]);
    if (val == null) return <p className="font-medium text-sm">$0</p>;
    return render(val);
  }

  // Employer-portal async calculators mirroring broker logic
  function AsyncEnrollValueLocal({
    plan,
    employee,
    coverageTier,
    dependents,
    pick,
  }: {
    plan: any;
    employee: any;
    coverageTier: string;
    dependents?: any[];
    pick: (r: any) => number;
  }) {
    const [val, setVal] = useState<number>(0);
    useEffect(() => {
      let mounted = true;
      const run = async () => {
        if (!plan || !employee) {
          setVal(0);
          return;
        }
        const pd = await fetchPlanPremiumData([plan.id], employerClientId);
        const planRates: PlanRate[] = (pd.ratesByPlanId[plan.id] || []) as PlanRate[];
        const planContribs = pd.contribsByPlanId[plan.id] || [];

        // Determine covered dependents if not provided
        let coveredDependents = Array.isArray(dependents) ? dependents : [];
        if (coveredDependents.length === 0 && employee?.id && plan?.id) {
          try {
            const [empEnrollmentsRes, employeeDependentsRes] =
              await Promise.all([
                fetch(`/api/enrollments?employeeId=${employee.id}`),
                fetch(`/api/employees/${employee.id}/dependents`),
              ]);
            const empEnrollments = empEnrollmentsRes.ok
              ? await empEnrollmentsRes.json()
              : [];
            const allEmployeeDependents = employeeDependentsRes.ok
              ? await employeeDependentsRes.json()
              : [];
            const enrolledDepIds = (empEnrollments as any[])
              .filter(
                (r) => r.planId === plan.id && Number.isFinite(r.dependentId)
              )
              .map((r) => r.dependentId as number);
            coveredDependents = (allEmployeeDependents as any[]).filter((d) =>
              enrolledDepIds.includes(d.id)
            );
          } catch {}
        }
        const result = calculateEnrollmentPremium({
          plan,
          planRates,
          planContributions: planContribs as any,
          employee,
          dependents: coveredDependents,
          coverageTier,
          allPlanContributions: pd.allPlanContributions,
          allPlanRates: pd.allPlanRates,
        }) as any;
        if (!mounted) return;
        setVal(pick(result) || 0);
      };
      run();
      return () => {
        mounted = false;
      };
    }, [plan?.id, employee?.id, coverageTier]);
    return (
      <>
        {val.toLocaleString(undefined, { style: "currency", currency: "USD" })}
      </>
    );
  }

  function AsyncTotalForType({
    enrollments,
    plansData,
    employees,
  }: {
    enrollments: any[];
    plansData: any[];
    employees: any[];
  }) {
    const [total, setTotal] = useState<number>(0);
    useEffect(() => {
      let mounted = true;
      const run = async () => {
        let sum = 0;
        const baseEnrollments = (enrollments || []).filter(
          (e: any) => e.dependentId == null
        );
        // DEV-262: one shared-loader call for every plan up front (was a
        // per-enrollment rate fetch + full contributions download INSIDE the
        // loop — N+1 requests). Also brings buy-up base-plan rows. Errors
        // fall back to empty maps, matching the old forgiving behavior.
        const planIds = Array.from(
          new Set(
            baseEnrollments
              .map((e: any) => Number(e.planId))
              .filter(Number.isFinite)
          )
        );
        let premiumData: Awaited<ReturnType<typeof fetchPlanPremiumData>>;
        try {
          premiumData = await fetchPlanPremiumData(planIds, employerClientId);
        } catch {
          premiumData = {
            allPlanContributions: [],
            allPlanRates: [],
            ratesByPlanId: {},
            contribsByPlanId: {},
            isLoading: false,
          };
        }
        for (const enr of baseEnrollments) {
          const plan = (plansData as any[]).find((p) => p.id === enr.planId);
          const emp = (employees as any[]).find((e) => e.id === enr.employeeId);
          if (!plan || !emp) continue;
          const planRates = premiumData.ratesByPlanId[plan.id] || [];
          const planContribs = premiumData.contribsByPlanId[plan.id] || [];

          // Resolve covered dependents for this enrollment
          let coveredDependents: any[] = [];
          try {
            const [empEnrollmentsRes, employeeDependentsRes] =
              await Promise.all([
                fetch(`/api/enrollments?employeeId=${emp.id}`),
                fetch(`/api/employees/${emp.id}/dependents`),
              ]);
            const empEnrollments = empEnrollmentsRes.ok
              ? await empEnrollmentsRes.json()
              : [];
            const allEmployeeDependents = employeeDependentsRes.ok
              ? await employeeDependentsRes.json()
              : [];
            const enrolledDepIds = (empEnrollments as any[])
              .filter(
                (r) => r.planId === plan.id && Number.isFinite(r.dependentId)
              )
              .map((r) => r.dependentId as number);
            coveredDependents = (allEmployeeDependents as any[]).filter((d) =>
              enrolledDepIds.includes(d.id)
            );
          } catch {}

          const result = calculateEnrollmentPremium({
            plan,
            planRates,
            planContributions: planContribs as any,
            employee: emp,
            dependents: coveredDependents,
            coverageTier: enr.coverageTier,
            // DEV-262: base-plan rows so buy-up employer shares resolve
            allPlanContributions: premiumData.allPlanContributions as any,
            allPlanRates: premiumData.allPlanRates as any,
          }) as any;
          sum += Number(result?.totalMonthlyRate || 0);
        }
        if (mounted) setTotal(Math.round(sum * 100) / 100);
      };
      run();
      return () => {
        mounted = false;
      };
    }, [JSON.stringify(enrollments), employerClientId]);
    return (
      <p className="text-lg font-bold">
        {total.toLocaleString(undefined, {
          style: "currency",
          currency: "USD",
        })}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Benefits & Plans</h1>
      </div>

      {/* Benefits Sub-navigation with compact button group */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as typeof BENEFITS_TABS[number])}
        className="space-y-4"
      >
        <div className="flex items-center gap-2 border-b pb-2">
          <span className="text-sm font-medium text-muted-foreground">
            View:
          </span>
          <TabsList className="h-9 bg-muted">
            <TabsTrigger value="overview" className="text-xs h-7">
              Overview
            </TabsTrigger>
            <TabsTrigger value="enrollments" className="text-xs h-7">
              Enrollments
            </TabsTrigger>
            <TabsTrigger value="open-enrollment" className="text-xs h-7">
              Open Enrollment
            </TabsTrigger>
            <TabsTrigger value="employee-summary" className="text-xs h-7">
              Employee Summary
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          <div className="space-y-6">
            {/* Pending Changes Section */}
            <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20 dark:border-orange-800">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-orange-800 dark:text-orange-200">
                  <Clock className="h-5 w-5" />
                  Pending Broker Changes
                </CardTitle>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Benefits modifications currently being processed by your
                  broker
                </p>
              </CardHeader>
              <CardContent>
                {(() => {
                  const rows: any[] = Array.isArray(ticketsView?.tasks)
                    ? (ticketsView!.tasks as any[])
                    : [];
                  
                  // Only show benefit-related tasks (enrollments, plan changes, QLEs)
                  // Exclude general broker tasks like "add census" that aren't benefit changes
                  const BENEFIT_CATEGORIES = ["benefits_enrollment", "plan_changes", "qle", "qualifying_life_event"];
                  
                  const pendingChanges = rows
                    .filter((t: any) => {
                      const status = String(t.status || "").toLowerCase();
                      const category = String(t.category || "").toLowerCase();
                      // Must be pending status AND a benefit-related category
                      return ["open", "in_progress", "pending_info"].includes(status) &&
                        BENEFIT_CATEGORIES.includes(category);
                    })
                    .map((t: any) => {
                      // Format category for display
                      const categoryLabels: Record<string, string> = {
                        benefits_enrollment: "Enrollment",
                        plan_changes: "Plan Change",
                        qle: "Life Event",
                        qualifying_life_event: "Life Event",
                      };
                      const category = String(t.category || "").toLowerCase();
                      
                      return {
                      id: t.id,
                      employeeName:
                        t.requesterName || t.requesterEmail || "Employee",
                      changeType: categoryLabels[category] || "Benefit Change",
                      description: t.title || t.description || "",
                      requestedDate: t.createdAt || new Date().toISOString(),
                      expectedCompletion: t.estimatedResolutionDate || null,
                      status: String(t.status || "in_progress")
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c: string) => c.toUpperCase()),
                      priority: String(t.priority || "standard")
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c: string) => c.toUpperCase()),
                    };
                    });

                  if (pendingChanges.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground">
                        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                          No pending broker changes at this time
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                        <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg border">
                          <div className="text-2xl font-bold text-orange-600">
                            {pendingChanges.length}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Total Pending
                          </div>
                        </div>
                        <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg border">
                          <div className="text-2xl font-bold text-red-600">
                            {
                              pendingChanges.filter(
                                (c) => c.priority === "High"
                              ).length
                            }
                          </div>
                          <div className="text-xs text-muted-foreground">
                            High Priority
                          </div>
                        </div>
                        <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg border">
                          <div className="text-2xl font-bold text-blue-600">
                            {
                              pendingChanges.filter(
                                (c) => c.status === "In Progress"
                              ).length
                            }
                          </div>
                          <div className="text-xs text-muted-foreground">
                            In Progress
                          </div>
                        </div>
                      </div>

                      <div className="border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-muted/50 border-b">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium">
                                  Employee
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium">
                                  Change Type
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium">
                                  Description
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium">
                                  Expected
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium">
                                  Priority
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {pendingChanges.map((change) => (
                                <tr
                                  key={change.id}
                                  className="hover:bg-muted/30"
                                >
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <User className="h-4 w-4 text-primary" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">
                                          {change.employeeName}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          Requested{" "}
                                          {format(
                                            new Date(change.requestedDate),
                                            "MMM dd"
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {change.changeType}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3">
                                    <p className="text-sm max-w-xs">
                                      {change.description}
                                    </p>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Badge
                                      variant={
                                        change.status === "In Progress"
                                          ? "default"
                                          : "secondary"
                                      }
                                      className="text-xs"
                                    >
                                      {change.status}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3">
                                    <p className="text-sm text-muted-foreground">
                                      {change.expectedCompletion
                                        ? format(
                                            new Date(change.expectedCompletion),
                                            "MMM dd, yyyy"
                                          )
                                        : "TBD"}
                                    </p>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Badge
                                      variant={
                                        change.priority === "High"
                                          ? "destructive"
                                          : "outline"
                                      }
                                      className="text-xs"
                                    >
                                      {change.priority}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <p className="text-xs text-muted-foreground">
                          Last updated:{" "}
                          {format(new Date(), "MMM dd, yyyy hh:mm a")}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                          >
                            Refresh Status
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                          >
                            Contact Broker
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Overview wired to broker logic */}
            <BenefitsOverview
              plansData={plansData as any}
              employees={employees as any}
              clientId={employerClientId}
              getEffectivePlan={getEffectivePlan as any}
              formatDateSafe={formatDateSafe as any}
            />

            {/* Enrollment Status card removed; covered within BenefitsOverview */}

            {/* Available Plans wired to broker logic, employer-safe actions */}
            <AvailablePlans
              plansData={planYearsData as any}
              // useClientPlans is disabled (so not "loading") until the
              // user's clientId is known — don't decide the default filter
              // from an empty list before then.
              plansLoading={plansLoading || !employerClientId}
              getEffectivePlan={getEffectivePlan as any}
              formatDateSafe={formatDateSafe as any}
              clientId={employerClientId}
              setLocation={setLocation}
              showDelete={false}
              showViewDetails={true}
              primaryActionLabel="Request Additional Plans"
              onPrimaryAction={() => setIsRequestPlansDialogOpen(true)}
            />

            {/* Premium ledger — per-month charged history (originals,
                reversals, re-charges) from the immutable charge ledger */}
            {user?.clientId != null && (
              <PremiumLedgerCard clientId={Number(user.clientId)} />
            )}

            <BenefitsDisplaySettingsCard />
          </div>
        </TabsContent>

        <TabsContent value="enrollments" className="space-y-4">
          <div className="mt-2">
            <EnrollmentsTab
              plansData={plansData as any}
              clientEmployees={employees as any}
              clientId={employerClientId}
              showCreate={false}
              isEmployerPortal={true}
            />
          </div>
        </TabsContent>

        <TabsContent value="open-enrollment" className="space-y-4">
          <OpenEnrollment
            clientEmployees={employees as any}
            clientId={employerClientId}
            plansData={plansData as any}
            planYearsData={planYearsData as any}
            plansLoading={plansLoading}
          />
        </TabsContent>

        <TabsContent value="employee-summary" className="space-y-4">
          <EmployeeSummary
            employees={employees as any}
            plansData={plansData as any}
            clientId={employerClientId}
          />
        </TabsContent>
      </Tabs>

      {/* Request Additional Plans Modal */}
      <Dialog
        open={isRequestPlansDialogOpen}
        onOpenChange={setIsRequestPlansDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Request Additional Plans
            </DialogTitle>
            <DialogDescription>
              Send a request to your broker for additional or alternative
              insurance plans for your organization.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="request-type">Request Type</Label>
              <Select value={requestType} onValueChange={setRequestType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="additional-health">
                    Additional Health Plans
                  </SelectItem>
                  <SelectItem value="dental-vision">
                    Dental & Vision Plans
                  </SelectItem>
                  <SelectItem value="life-disability">
                    Life & Disability Insurance
                  </SelectItem>
                  <SelectItem value="voluntary-benefits">
                    Voluntary Benefits
                  </SelectItem>
                  <SelectItem value="alternative-carriers">
                    Alternative Carriers
                  </SelectItem>
                  <SelectItem value="cost-reduction">
                    Cost Reduction Options
                  </SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="urgency">Urgency Level</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger>
                  <SelectValue placeholder="Select urgency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - General inquiry</SelectItem>
                  <SelectItem value="normal">
                    Normal - Within 2 weeks
                  </SelectItem>
                  <SelectItem value="high">High - Within 1 week</SelectItem>
                  <SelectItem value="urgent">Urgent - ASAP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="request-message">Message</Label>
              <Textarea
                id="request-message"
                placeholder="Describe your specific needs, current challenges, budget considerations, or any other details that would help your broker provide the best recommendations..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsRequestPlansDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const requestData = {
                  type: requestType,
                  message: requestMessage,
                  urgency: urgency,
                  requestedAt: new Date().toISOString(),
                  companyName: "Your Company", // This would come from user context
                };
                sendPlanRequestMutation.mutate(requestData);
              }}
              disabled={
                !requestType ||
                !requestMessage.trim() ||
                sendPlanRequestMutation.isPending
              }
            >
              {sendPlanRequestMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
